import { Fragment } from 'react';
import {
   Alert,
   AlertIcon,
   Box,
   Heading,
   Flex,
   Text,
   Divider,
   Image,
   List,
   ListItem,
   SimpleGrid,
   Link,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';
import { _js, ___p } from '@ifixit/localize';

type ConditionalDisplaysType = {
   statusNotifications: any[];
   userBio: string;
   teams: any[];
   miscLink: any;
   actions: any[];
};

type UserSummaryType = {
   name: string;
   username: string;
   avatarLink: string;
   avatarSrc: string;
   stats: any;
   dateJoined: string;
};

export function UserCard({
   summaryData,
   conditionalData,
}: {
   summaryData: UserSummaryType;
   conditionalData: ConditionalDisplaysType;
}) {
   return (
      <Flex
         w={{ base: '100%', lg: '316px' }}
         direction="column"
         padding="8px 14px 14px"
         backgroundColor="gray.100"
         borderRadius="lg"
      >
         <UserSummary {...summaryData} />
         <ConditionalDisplays {...conditionalData} />
      </Flex>
   );
}

const UserSummary = ({
   name,
   username,
   avatarLink,
   avatarSrc,
   stats,
   dateJoined = '',
}: UserSummaryType) => (
   <Fragment>
      <Heading as="h1" id="aboutBoxName">
         {name}
      </Heading>
      {username && <Text m="0">@{username}</Text>}
      <Flex mt={2}>
         <Link href={avatarLink}>
            <Image borderRadius="md" boxSize="96px" src={avatarSrc} />
         </Link>
         <UserStats {...stats} dateJoined={dateJoined} />
      </Flex>
   </Fragment>
);

const UserStats = ({
   reputation,
   answers,
   guides,
   dateJoined,
}: {
   reputation: any;
   answers: any;
   guides: any;
   dateJoined: string;
}) => (
   <Flex direction="column" justify="space-between" alignItems="center" paddingLeft="3em">
      <SimpleGrid templateColumns="repeat(2, auto)">
         {reputation && (
            <Fragment>
               <Text my={1} align="right" id="numReputation">
                  {reputation}
               </Text>
               <Text my={1} ml={2}>
                  {_js('Reputation')}
               </Text>
            </Fragment>
         )}
         {answers && (
            <Fragment>
               <Text my={1} align="right">
                  {answers}
               </Text>
               <Text my={1} ml={2}>
                  {_js('Solutions')}
               </Text>
            </Fragment>
         )}
         {guides && (
            <Fragment>
               <Text my={1} align="right">
                  {guides}
               </Text>
               <Text my={1} ml={2}>
                  {_js('Guides')}
               </Text>
            </Fragment>
         )}
      </SimpleGrid>
      <Divider marginBottom="4px" />
      {dateJoined && (
         <Text my={1} fontSize="sm" as="em" textAlign="center">
            {`${_js('Member Since')} ${dateJoined}`}
         </Text>
      )}
   </Flex>
);

const ConditionalDisplays = ({
   statusNotifications = [],
   userBio = '',
   teams = [],
   miscLink,
   actions = [],
}: ConditionalDisplaysType) => {
   const showStatuses = statusNotifications.length > 0;
   const showUserBio = userBio.length > 0;
   const showTeams = teams.length > 0;
   const showActions = actions.length > 0;

   return (
      <Fragment>
         {showStatuses && <StatusNotifications statuses={statusNotifications} />}
         {showUserBio && <Box dangerouslySetInnerHTML={{ __html: userBio }} />}
         {showTeams && <TeamsDisplay teams={teams} />}
         {miscLink && (
            <Link fontSize="md" fontWeight="bold" href={miscLink.url}>
               {miscLink.text}
            </Link>
         )}
         {showActions && <Actions actions={actions} />}
      </Fragment>
   );
};

const StatusNotifications = ({ statuses }: { statuses: any[] }) => (
   <>
      {statuses.map((status: any) => (
         <Alert key={status.message} status={status.type} my={1} py={1}>
            <AlertIcon />
            <Text my={0}>{status.message}</Text>
         </Alert>
      ))}
   </>
);

const TeamsDisplay = ({ teams }: { teams: any[] }) => (
   <Fragment>
      <Heading as="h3" fontSize="2xl" color="black">
         {___p(teams.length, 'Team', 'Teams')}
      </Heading>
      <TeamsList teams={teams} />
   </Fragment>
);

const TeamsList = ({ teams }: { teams: any[] }) => (
   <List p={0}>
      {teams.map((team: any) => (
         <Fragment key={team.name}>
            <TeamItem {...team} />
            <Divider m={0} />
         </Fragment>
      ))}
   </List>
);

const TeamItem = ({
   avatar,
   url,
   name,
   studentCode,
   description,
}: {
   avatar: string;
   url: string;
   name: string;
   studentCode: string;
   description: string;
}) => (
   <ListItem py={2}>
      <Flex>
         <Image boxSize="56px" src={avatar} mr={1} />
         <Flex direction="column">
            <Link href={url}>
               <Text fontSize="md" my={0} color="brand.500">
                  {name}
               </Text>
            </Link>
            {studentCode && (
               <Text fontSize="md">
                  {_js('Team Tag')}: {studentCode}
               </Text>
            )}
            <Text fontSize="md" m="2px 0 0">
               {description}
            </Text>
         </Flex>
      </Flex>
   </ListItem>
);

const Actions = ({ actions }: { actions: any[] }) => (
   <List p={0}>
      {actions.map((action: any) => (
         <ActionItem key={action.text} {...action} />
      ))}
   </List>
);

const ActionItem = ({ text, url, id }: { text: string; url: string; id: any }) => (
   <ListItem color="brand.500" my={1}>
      <FontAwesomeIcon icon={faCircleInfo} />
      <Link ml={1} href={url} data-testid={id}>
         {text}
      </Link>
   </ListItem>
);
